import axios from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../auth/tokenStore';

const DEV_API_URL = 'http://localhost:5100/';
const PROD_API_URL = 'https://bimcheck.dibs42.com/api/';

const baseURL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? PROD_API_URL : DEV_API_URL);

const instance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the stored access token to every request.
instance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, try a single refresh + retry; otherwise clear the session.
let refreshPromise = null;

async function runRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await axios.post(
      `${baseURL}Auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    setTokens({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken });
    return res.data.accessToken;
  } catch {
    clearTokens();
    return null;
  }
}

// Returns a usable access token, refreshing only if no other tab/request already did.
// Coordinated across tabs via the Web Locks API so concurrent tabs don't each rotate
// the shared refresh token and trip server-side reuse-detection (which logs everyone out).
// `failedToken` is the token that produced the 401.
async function getFreshToken(failedToken) {
  const current = getAccessToken();
  if (current && current !== failedToken) return current; // already rotated elsewhere

  const doRefresh = async () => {
    const latest = getAccessToken();
    if (latest && latest !== failedToken) return latest; // rotated while we waited for the lock
    return runRefresh();
  };

  if (navigator.locks?.request) {
    return navigator.locks.request('bc-token-refresh', doRefresh);
  }
  // Fallback (no Web Locks API): in-tab de-dup only.
  refreshPromise = refreshPromise || doRefresh();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (response && response.status === 503) {
      window.location.href = '/maintenance.html';
      return;
    }

    const isAuthCall = config?.url?.includes('Auth/login') || config?.url?.includes('Auth/refresh');
    if (response && response.status === 401 && config && !config._retry && !isAuthCall && getRefreshToken()) {
      config._retry = true;
      const failedToken = (config.headers?.Authorization || '').replace('Bearer ', '') || getAccessToken();
      const newToken = await getFreshToken(failedToken);

      if (newToken) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return instance(config);
      }

      clearTokens();
      if (window.location.pathname !== '/login') window.location.assign('/login');
    }

    return Promise.reject(error);
  }
);

export default instance;
